import classNames from 'classnames';
import * as React from 'react';
import * as _ from 'lodash';

import './index.scss';
import * as Edge from '../../core';
import Input from '../global/input';
import { connect } from 'react-redux';
import { AppState } from '../../store';
import { getPriceTitle, getPriceUnit } from '../../utilities/commerceUtil';
import * as StrUtil from '../../utilities/strUtil';

export interface TotalCostProps {
	allowEdit?: boolean;
	allPrices: Edge.Models.Price[];
	coupon?: Edge.Models.Coupon;
	numberOfGlasses: number;
	onQuantityChanged?: (quantity: number) => void;
	order?: Edge.Models.CreateCheckoutSessionResponse;
	overridePriceAmount?: number;
	price: Edge.Models.Price;
	quantity: number;
}

const isPrimary = (i?: Edge.Models.Price) => {
	return (
		i &&
		(i.priceType === Edge.Models.PriceTypes.Recurring ||
			i.priceType === Edge.Models.PriceTypes.Sessions ||
			i.priceType === Edge.Models.PriceTypes.Subscription)
	);
};

const orderQuantityLine = (item: Edge.Models.CheckoutSessionItemResponse, price?: Edge.Models.Price) => {
	const { amount, quantity, type } = item;
	let { description } = item;
	if (type === Edge.Models.CheckoutSessionItemType.Shipping) {
		description = 'Shipping';
	}
	const perItemPrice = Math.ceil((amount / quantity) * 100) / 100;
	if (!price || !isPrimary(price)) {
		if (item.amount) {
			if (item.quantity) {
				return `$${perItemPrice} x ${quantity} ${description}`;
			} else {
				return `$${amount} ${description}`;
			}
		} else {
			if (quantity) {
				return `${quantity} ${description}`;
			} else {
				return `${description}`;
			}
		}
	}
	switch (price.priceType) {
		case Edge.Models.PriceTypes.Sessions:
			return `${perItemPrice} x ${item.quantity}`;
		case Edge.Models.PriceTypes.Recurring:
		// Falls through
		case Edge.Models.PriceTypes.Subscription:
			return `${perItemPrice} x ${item.quantity} ${StrUtil.pluralize(getPriceUnit(price), item.quantity)}`;
		default:
			return undefined;
	}
};

export class TotalCostBody extends React.PureComponent<TotalCostProps> {
	public render() {
		const { order, price, allPrices, quantity, numberOfGlasses, overridePriceAmount } = this.props;
		if (order) {
			const priceById = _.mapValues(_.groupBy(allPrices, (i) => i.id), (i) => i[0]);
			const withPrice = order.checkoutSessionItems.map((i) => {
				return { price: priceById[i.id], item: i };
			});
			const primaryItems = withPrice.filter((i) => isPrimary(i.price));
			const secondaryItems = withPrice.filter((i) => !isPrimary(i.price));
			return (
				<>
					{primaryItems.map((i, ix) => (
						<div className="totalCostCard" key={ix}>
							<div className="titleContain">
								<p className="title">{getPriceTitle(i.price)}</p>
								<p className="secondary">${orderQuantityLine(i.item, i.price)}</p>
							</div>
							<div className="costContain">
								<p className="cost">${i.item.amount}</p>
							</div>
						</div>
					))}
					{secondaryItems.length > 0 && (
						<div className="totalCostCard">
							<div className="titleContain">
								<p className="title">Total After Glasses</p>
								{secondaryItems.map((i, ix) => {
									return (
										<p className="secondary" key={ix}>
											{orderQuantityLine(i.item, i.price)}
										</p>
									);
								})}
							</div>
							<div className="costContain">
								<p className="cost">${order.amount}</p>
							</div>
						</div>
					)}
				</>
			);
		} else {
			const glassesPrice = allPrices.filter((i) => i.priceType === Edge.Models.PriceTypes.Glasses)[0];
			const amount = overridePriceAmount === undefined ? price.unitAmount : overridePriceAmount;
			return (
				<>
					<div className="totalCostCard">
						<div className="titleContain">
							<p className="title">{getPriceTitle(price)}</p>
							<p className="secondary">${this.quantityLine(price, quantity, amount)}</p>
						</div>
						<div className="costContain">
							<p className={classNames('cost', { oldCost: this.props.coupon })}>
								${this.totalPrice(amount * quantity, undefined)}
							</p>
						</div>
						{this.props.coupon && (
							<>
								<div />
								<div className="costContain">
									<p className="cost">${this.totalPrice(amount * quantity, this.props.coupon)}</p>
								</div>
							</>
						)}
					</div>
					{!!numberOfGlasses && !!glassesPrice && (
						<div className="totalCostCard">
							<div className="titleContain">
								<p className="title">{getPriceTitle(glassesPrice)}</p>
								<p className="secondary">
									${glassesPrice.unitAmount} x {numberOfGlasses}
								</p>
							</div>
							<div className="costContain">
								<p
									className={classNames('cost', {
										oldCost: this.props.coupon && this.props.coupon.percentOff,
									})}
								>
									${this.totalPrice(glassesPrice.unitAmount * numberOfGlasses, undefined)}
								</p>
							</div>
							{/* since we're only showing the price for this line, any flat-off amount will be in the first line */}
							{this.props.coupon && this.props.coupon.percentOff && (
								<>
									<div />
									<div className="costContain">
										<p className="cost">
											$
											{this.totalPrice(
												glassesPrice.unitAmount * numberOfGlasses,
												this.props.coupon
											)}
										</p>
									</div>
								</>
							)}
						</div>
					)}
				</>
			);
		}
	}

	private quantityLine = (price: Edge.Models.Price, quantity: number, amount: number) => {
		const quantityRender = this.props.allowEdit ? (
			<Input type="number" value={quantity} onChange={this.onQuantityChange} min={1} />
		) : (
			quantity
		);
		switch (price.priceType) {
			case Edge.Models.PriceTypes.Glasses:
				return (
					<span>
						{amount} x {quantityRender} {StrUtil.pluralize(getPriceUnit(price), quantity)}
					</span>
				);
			case Edge.Models.PriceTypes.Sessions:
				return (
					<span>
						{amount} x {quantityRender} {StrUtil.pluralize(getPriceUnit(price), quantity)} of{' '}
						{price.sessions}
					</span>
				);
			case Edge.Models.PriceTypes.Recurring:
			// Falls through
			case Edge.Models.PriceTypes.Subscription:
				return (
					<span>
						{amount} x {quantityRender} {StrUtil.pluralize(getPriceUnit(price), quantity)}
					</span>
				);
			default:
				return undefined;
		}
	};

	private onQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = this.props.price.priceType === Edge.Models.PriceTypes.Recurring ? 1 : parseInt(e.target.value);
		if (this.props.onQuantityChanged && newValue > 0) {
			this.props.onQuantityChanged(newValue);
		}
	};

	private totalPrice = (originalPrice: number, coupon?: Edge.Models.Coupon) => {
		var discountedPrice = originalPrice;

		if (coupon) {
			if (coupon.amountOff) {
				discountedPrice = originalPrice - coupon.amountOff;

				// If less than zero, return 0
				discountedPrice = discountedPrice >= 0 ? discountedPrice : 0;
			} else if (coupon.percentOff) {
				discountedPrice = originalPrice * (1 - coupon.percentOff / 100);
			} else {
				discountedPrice = originalPrice;
			}
		}

		return discountedPrice.toFixed(2);
	};
}

const TotalCost: React.FC<TotalCostProps> = (props) => {
	return (
		<div className="totalCost">
			<h3 className="cardTitle">Total Cost</h3>
			<TotalCostBody {...props} />
		</div>
	);
};

export default connect(({ priceState }: AppState) => {
	return { allPrices: priceState.data! };
})(TotalCost);
